import React, { useMemo, useState } from 'react';
import { SectionCard } from '@frontend/app/containers/Projects/CampaignPage/components/SectionCard';
import { Button } from '@frontend/shadcn/components/ui/button';
import { P } from '@frontend/shadcn/components/typography/p';
import { Badge } from '@frontend/shadcn/components/ui/badge';
import { Card, CardContent } from '@frontend/shadcn/components/ui/card';
import { TemplateTransportType, TemplateType } from '@frontend/app/types/globalTypes';
import { MessageTemplatesQuery_templates } from '@frontend/app/queries/types/MessageTemplatesQuery';
import { useGetMessageTemplates } from '@frontend/app/hooks';
import { Skeleton } from '@frontend/shadcn/components/ui/skeleton';
import { Empty } from '@frontend/app/refresh-components';
import { EnvelopeIcon, PenIcon, PlusIcon } from '@revfluence/fresh-icons/solid/esm';
import { EmailTemplateDrawer } from './EmailTemplateDrawer';

interface EmailTemplatesProps {
  projectId: number;
  templateType: TemplateType;
  title: string;
  description: string;
  defaultTemplateId: number;
  defaultEmailTitle: string;
  defaultEmailSubject: string;
  defaultEmailText: any;
  handleMakeDefault: (templateId: number) => Promise<void>;
  emptyStateTitle?: string;
  emptyStateDescription?: string;
}

export const EmailTemplates: React.FC<EmailTemplatesProps> = ({
  projectId,
  templateType,
  title,
  description,
  defaultTemplateId,
  defaultEmailTitle,
  defaultEmailSubject,
  defaultEmailText,
  handleMakeDefault,
  emptyStateTitle = 'No email templates found',
  emptyStateDescription = 'Create email templates to send to the creators.',
}) => {
  const { templates, loading: messageTemplatesLoading, refetch } = useGetMessageTemplates();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<MessageTemplatesQuery_templates | null>(null);

  const filteredEmailTemplates = useMemo(() => {
    if (messageTemplatesLoading) return [];
    return templates.filter(
      (template) =>
        template.type === templateType &&
        template.programId === projectId &&
        template.transportType === TemplateTransportType.EMAIL,
    );
  }, [messageTemplatesLoading, templates, templateType, projectId]);

  const loading = messageTemplatesLoading;

  const handleEditClick = (template: MessageTemplatesQuery_templates) => {
    setSelectedTemplate(template);
    setIsDrawerOpen(true);
  };

  const handleCreateClick = () => {
    setSelectedTemplate(null);
    setIsDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
    setSelectedTemplate(null);
  };

  const handleMutationSuccess = () => {
    refetch();
    handleDrawerClose();
  };

  return (
    <>
      <SectionCard
        title={title}
        description={description}
        rightContent={
          <Button variant="outline" size="xs" onClick={handleCreateClick} className="self-start mt-0.5">
            <PlusIcon className="mr-1.5" />
            Add Email Template
          </Button>
        }
      >
        <div className="flex flex-col gap-4">
          {loading && <Skeleton data-testid="skeleton" className="h-10 w-full" />}
          {!loading && filteredEmailTemplates.length === 0 && (
            <Empty
              icon={EnvelopeIcon}
              title={emptyStateTitle}
              description={emptyStateDescription}
              primaryCtaProps={{
                text: 'Create Template',
                onClick: handleCreateClick,
              }}
              layout="horizontal"
              size="lg"
              withBackground
            />
          )}
          {!loading && filteredEmailTemplates.map((template) => (
            <Card key={template.id} className="rounded-2xl border border-border">
              <CardContent className="px-4 py-2 flex items-center justify-between">
                <P className="mb-0" fontWeight="font-medium">{template.title}</P>
                <div className="flex items-center gap-2">
                  {defaultTemplateId === template.id && (
                    <Badge variant="secondary">Default</Badge>
                  )}
                  <Button
                    data-testid="edit-button"
                    variant="ghost"
                    size="icon"
                    onClick={() => handleEditClick(template)}
                  >
                    <PenIcon />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </SectionCard>
      <EmailTemplateDrawer
        open={isDrawerOpen}
        onOpenChange={setIsDrawerOpen}
        template={selectedTemplate}
        projectId={projectId}
        onSaveSuccess={handleMutationSuccess}
        onCancel={handleDrawerClose}
        onDeleteSuccess={handleMutationSuccess}
        defaultTemplateId={defaultTemplateId}
        defaultEmailTitle={defaultEmailTitle}
        defaultEmailSubject={defaultEmailSubject}
        defaultEmailText={defaultEmailText}
        templateType={templateType}
        handleMakeDefault={handleMakeDefault}
      />
    </>
  );
};
